import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useNonBrandedContext } from "../context/use-non-branded";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useSearchTermsGridQuery } from "src/context/api/non-branded/api";
import { fDate } from "src/utils/format-time";
import { ColumnFeatures } from "./features";
import * as searchTermsColumns from "./search-terms/columns";
import * as competitorsColumns from "./competitors/index";
import { buildRankCell, buildSimpleBarCell, buildSplitCell } from "./search-terms/columns/build";
import useLocales from "src/locales/use-locales";
import { LightTooltip } from "./tooltips/light";
import { Stack, Typography } from "@mui/material";
import { useMemo, useRef } from "react";
import DiffIcon from "../diff-icon";

const getResponsiveColumnSize = (column: any) => {
    const features = column.features || [];
    const hasFeature = (feature: ColumnFeatures) => features.includes(feature);

    if (column.field === 'search_term') {
        return { minWidth: 195, flex: 1.9 };
    }

    if (column.field === 'opportunity') {
        return { minWidth: 120, flex: 1.2 };
    }

    if (hasFeature(ColumnFeatures.SPLIT)) {
        return { minWidth: 170, flex: 1.8 };
    }

    if (hasFeature(ColumnFeatures.SIMPLEBAR)) {
        return { minWidth: 150, flex: 1.4 };
    }

    if (hasFeature(ColumnFeatures.RANK)) {
        return { minWidth: 120, flex: 1.15 };
    }

    return { minWidth: 80, flex: 0.9 };
};

export const useSearchTermsGridData = (
    paginationModel: GridPaginationModel & GridFilterModel & { countries: string[] },
    sortModel: any,
) => {
    const workspace = useCurrentWorkspace();
    const { selectedOpportunity } = useNonBrandedContext();
    const start = useSelector((state: RootState) => state.filters.start);
    const end = useSelector((state: RootState) => state.filters.end);
    const compare = useSelector((state: RootState) => state.filters.compare);
    const compareStart = useSelector((state: RootState) => state.filters.compareStart);
    const compareEnd = useSelector((state: RootState) => state.filters.compareEnd);
    const devices = useSelector((state: RootState) => state.filters.devices);
    const platforms = useSelector((state: RootState) => state.filters.platforms);
    const searchTerms = useSelector((state: RootState) => state.filters.keywords);
    const {
        data: searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    } = useSearchTermsGridQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(start, 'yyyy-MM-dd'),
        end_date: fDate(end, 'yyyy-MM-dd'),
        start_compare: fDate(compareStart, 'yyyy-MM-dd'),
        end_compare: fDate(compareEnd, 'yyyy-MM-dd'),
        devices: devices,
        countries: paginationModel.countries || [],
        search_terms: searchTerms,
        platforms: platforms,
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        filterModel: paginationModel.items,
        opportunity: selectedOpportunity,
        sort_field: sortModel[0]?.field,
        sort_order: sortModel[0]?.sort as string,
        compare: compare,
    });

    return {
        job_id: searchTermsGrid?.job_id,
        searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    };
};

export const useBuildColumns = (source: 'search-terms' | 'competitors') => {
    const { t } = useLocales();
    const popperRef = useRef<{ rowId: string | null }>({ rowId: null });
    const compare = useSelector((state: RootState) => state.filters.compare);
    const columns_rendered: any[] = [];
    const columns = source === 'search-terms' ? searchTermsColumns : competitorsColumns;
    Object.values(columns).sort((a: any, b: any) => a.position - b.position).forEach((column: any) => {
        const { features, color, headerTooltip, renderCell } = column;
        const responsiveSize = getResponsiveColumnSize(column);
        const minWidth = Math.max(column.width || 0, responsiveSize.minWidth);

        const render = useMemo(() => {
            if (renderCell) return renderCell;
            if (features?.includes(ColumnFeatures.SIMPLEBAR)) return buildSimpleBarCell({ ...column, color });
            if (features?.includes(ColumnFeatures.RANK)) return buildRankCell(column, popperRef);
            if (features?.includes(ColumnFeatures.SPLIT)) return buildSplitCell(column, popperRef);
            return undefined;
        }, [renderCell, features, color]);

        const renderCompare = useMemo(() => (params: any) => {
            if (!render) return undefined;
            const row = Object.assign({}, params.row, params.row.compare);
            const params_ = Object.assign({}, params, { row });
            return render(params_);
        }, [render]);

        const renderDiff = useMemo(() => (params: any) => {
            const isGood = column.diff?.good?.(column.field)(params);
            return (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '52px' }}
                >
                    <DiffIcon
                        plus={params.row.diff[column.field] > 0}
                        minus={params.row.diff[column.field] < 0}
                        good={isGood}
                        bad={!isGood}
                        evolution={params.row.diff[column.field]}
                        format={column.format}
                    />
                </Stack>
            )
        }, [render]);

        const baseColumn = [{
            ...column,
            renderCell: render,
            headerName: t(column.headerName),
            minWidth,
            flex: responsiveSize.flex,
            renderHeader() {
                return (
                    <LightTooltip title={t(headerTooltip)}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {t(column.headerName)}
                            </Typography>
                        </Stack>
                    </LightTooltip>
                );
            },
        }]

        if (compare && column.features?.includes(ColumnFeatures.COMPARISON)) {
            baseColumn.push({
                ...column,
                renderCell: renderCompare,
                headerName: t(column.headerName) + ' (' + t('dashboard.nb_brand_grid.columns.title.compare') + ')',
                field: column.field + '_compare',
                cellClassName: 'column--data-column compare-column',
                minWidth,
                flex: responsiveSize.flex,
                renderHeader() {
                    return (
                        <LightTooltip title={t(headerTooltip)}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {t(column.headerName)} ({t('dashboard.nb_brand_grid.columns.title.compare')})
                            </Typography>
                        </LightTooltip>
                    );
                },
            })
        }
        if (compare && column.features?.includes(ColumnFeatures.DIFF)) {
            baseColumn.push({
                ...column,
                field: column.field + '_diff',
                renderCell: renderDiff,
                headerName: t(column.headerName) + ' (' + t('dashboard.nb_brand_grid.columns.title.diff') + ')',
                minWidth,
                flex: responsiveSize.flex,
                renderHeader() {
                    return (
                        <LightTooltip title={t(headerTooltip)}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {t(column.headerName)} ({t('dashboard.nb_brand_grid.columns.title.diff')})
                            </Typography>
                        </LightTooltip>
                    );
                },
            })
        }

        columns_rendered.push(...baseColumn);
    });
    return columns_rendered;
};